import { useState } from "react";
import { MdOutlineDescription } from "react-icons/md";
import { Order } from "../utils/type";
import { validateTaxIdNumber } from "@components/form/validate/validateTaxIdNumber";
import { validateInvoiceCarrier } from "@components/form/validate/validateInvoiceCarrier";

interface Props {
  order: Order;
  setOrder: React.Dispatch<React.SetStateAction<Order>>;
}

const TaxInvoiceCard = ({ order, setOrder }: Props) => {
  const [taxIdError, setTaxIdError] = useState<string | null>(null);
  const [carrierError, setCarrierError] = useState<string | null>(null);

  const handleTaxIdChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 8);
    setOrder((prev) => ({ ...prev, taxId: digitsOnly }));
    if (taxIdError && digitsOnly.length === 8) {
      setTaxIdError(validateTaxIdNumber(digitsOnly) ? null : "請輸入有效的統一編號（8 位數字且符合檢查碼）");
    } else if (taxIdError && digitsOnly.length < 8) {
      setTaxIdError(null);
    }
  };

  const handleTaxIdBlur = () => {
    const v = order.taxId.trim();
    if (!v) {
      setTaxIdError(null);
      return;
    }
    if (v.length !== 8) {
      setTaxIdError("統一編號請輸入 8 位數字");
      return;
    }
    setTaxIdError(validateTaxIdNumber(v) ? null : "請輸入有效的統一編號（8 位數字且符合檢查碼）");
  };

  const handleCarrierChange = (value: string) => {
    const upper = value.toUpperCase();
    let next = upper;
    if (upper.length > 0 && upper[0] !== "/") {
      next = "/" + upper.replace(/\//g, "").slice(0, 7);
    } else if (upper.startsWith("/")) {
      next = "/" + upper.slice(1).replace(/[^0-9A-Z.+-]/g, "").slice(0, 7);
    }
    setOrder((prev) => ({ ...prev, carrier: next }));
    if (carrierError && next.length === 8) {
      setCarrierError(validateInvoiceCarrier(next) ? null : "請輸入有效的載具條碼（/ 開頭共 8 碼，例：/ABC1234）");
    } else if (carrierError) {
      setCarrierError(null);
    }
  };

  const handleCarrierBlur = () => {
    const v = order.carrier.trim();
    if (!v) {
      setCarrierError(null);
      return;
    }
    setCarrierError(validateInvoiceCarrier(v) ? null : "請輸入有效的載具條碼（/ 開頭共 8 碼，例：/ABC1234）");
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg">
      <div className="flex items-center space-x-3 mb-4">
        <div className="p-2.5 bg-orange-100 rounded-xl">
          <MdOutlineDescription className="text-orange-600 w-4 h-4" />
        </div>
        <h2 className="text-md font-bold text-gray-800">統編 / 載具</h2>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-600 mb-2.5">
          統一編號（選填）
        </label>
        <input
          type="text"
          inputMode="numeric"
          value={order.taxId}
          onChange={(e) => handleTaxIdChange(e.target.value)}
          onBlur={handleTaxIdBlur}
          className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all ${taxIdError ? "border-red-400" : "border-gray-200"}`}
          placeholder="請輸入 8 位數統編"
          maxLength={8}
        />
        {taxIdError && <p className="mt-1.5 text-sm text-red-500">{taxIdError}</p>}
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-600 mb-2.5">
          電子發票載具（選填）
        </label>
        <input
          type="text"
          value={order.carrier}
          onChange={(e) => handleCarrierChange(e.target.value)}
          onBlur={handleCarrierBlur}
          className={`w-full p-3.5 border-2 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all ${carrierError ? "border-red-400" : "border-gray-200"}`}
          placeholder="例：/ABC1234（/ 開頭共 8 碼）"
          maxLength={8}
        />
        {carrierError && <p className="mt-1.5 text-sm text-red-500">{carrierError}</p>}
      </div>
    </div>
  );
};

export default TaxInvoiceCard;
